const storageKey = (appId, vaultKey) => `tapcart-storefront-bridge:${appId}:vault:${vaultKey}`;
const readVault = (appId, vaultKey) => {
    if (typeof window === "undefined")
        return {};
    try {
        const raw = window.localStorage.getItem(storageKey(appId, vaultKey));
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === "object" && !Array.isArray(parsed)
            ? parsed
            : {};
    }
    catch (_a) {
        return {};
    }
};
// Returns `null` on success, or the Error when persistence fails (quota,
// privacy mode, serialization). Callers MUST surface a failure via
// `onError` rather than reporting success — otherwise a block believes its
// write committed when nothing was stored. SSR (no window) is a no-op, not
// a failure: there's nothing to persist server-side and blocks re-run on
// the client.
const writeVault = (appId, vaultKey, vault) => {
    if (typeof window === "undefined")
        return null;
    try {
        window.localStorage.setItem(storageKey(appId, vaultKey), JSON.stringify(vault));
        return null;
    }
    catch (err) {
        console.warn("[storefront-bridge] vault persist failed", err);
        return err instanceof Error ? err : new Error("vault persist failed");
    }
};
const requireVaultKey = (data, handlers) => {
    var _a;
    const payload = (data !== null && data !== void 0 ? data : {});
    if (!payload.vaultKey) {
        (_a = handlers.onError) === null || _a === void 0 ? void 0 : _a.call(handlers, new Error("vault: vaultKey required"));
        return null;
    }
    return payload;
};
export const vaultInitialize = (data, handlers) => {
    var _a;
    if (!requireVaultKey(data, handlers))
        return;
    // localStorage needs no provisioning — initialize just validates input.
    (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, {});
};
export const vaultGet = (data, handlers, ctx) => {
    var _a, _b, _c;
    const payload = requireVaultKey(data, handlers);
    if (!payload)
        return;
    if (!payload.key) {
        (_a = handlers.onError) === null || _a === void 0 ? void 0 : _a.call(handlers, new Error("vault/get: key required"));
        return;
    }
    const vault = readVault(ctx.config.appId, payload.vaultKey);
    (_b = handlers.onSuccess) === null || _b === void 0 ? void 0 : _b.call(handlers, { value: (_c = vault[payload.key]) !== null && _c !== void 0 ? _c : null });
};
export const vaultSet = (data, handlers, ctx) => {
    var _a, _b, _c, _d;
    const payload = requireVaultKey(data, handlers);
    if (!payload)
        return;
    if (!payload.key) {
        (_a = handlers.onError) === null || _a === void 0 ? void 0 : _a.call(handlers, new Error("vault/set: key required"));
        return;
    }
    const vault = readVault(ctx.config.appId, payload.vaultKey);
    vault[payload.key] = (_b = payload.value) !== null && _b !== void 0 ? _b : "";
    const err = writeVault(ctx.config.appId, payload.vaultKey, vault);
    if (err) {
        (_c = handlers.onError) === null || _c === void 0 ? void 0 : _c.call(handlers, err);
        return;
    }
    (_d = handlers.onSuccess) === null || _d === void 0 ? void 0 : _d.call(handlers, {});
};
export const vaultRemove = (data, handlers, ctx) => {
    var _a, _b, _c;
    const payload = requireVaultKey(data, handlers);
    if (!payload)
        return;
    if (!payload.key) {
        (_a = handlers.onError) === null || _a === void 0 ? void 0 : _a.call(handlers, new Error("vault/remove: key required"));
        return;
    }
    const vault = readVault(ctx.config.appId, payload.vaultKey);
    delete vault[payload.key];
    const err = writeVault(ctx.config.appId, payload.vaultKey, vault);
    if (err) {
        (_b = handlers.onError) === null || _b === void 0 ? void 0 : _b.call(handlers, err);
        return;
    }
    (_c = handlers.onSuccess) === null || _c === void 0 ? void 0 : _c.call(handlers, {});
};
export const vaultClear = (data, handlers, ctx) => {
    var _a, _b;
    const payload = requireVaultKey(data, handlers);
    if (!payload)
        return;
    const err = writeVault(ctx.config.appId, payload.vaultKey, {});
    if (err) {
        (_a = handlers.onError) === null || _a === void 0 ? void 0 : _a.call(handlers, err);
        return;
    }
    (_b = handlers.onSuccess) === null || _b === void 0 ? void 0 : _b.call(handlers, {});
};
export const vaultKeys = (data, handlers, ctx) => {
    var _a;
    const payload = requireVaultKey(data, handlers);
    if (!payload)
        return;
    const vault = readVault(ctx.config.appId, payload.vaultKey);
    (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, { keys: Object.keys(vault) });
};
export const vaultObserve = (data, handlers, ctx) => {
    var _a, _b, _c;
    const payload = requireVaultKey(data, handlers);
    if (!payload)
        return;
    if (!payload.key) {
        (_a = handlers.onError) === null || _a === void 0 ? void 0 : _a.call(handlers, new Error("vault/observe: key required"));
        return;
    }
    // Partial implementation: responds once with the current value. True
    // observation (re-invoking the handler on every change) needs the SDK's
    // repeated-callback semantics wired through the event bus — tracked as
    // follow-up work. One-shot reads cover the common "read on mount" use.
    console.warn("[storefront-bridge] vault/observe responds once on web; change observation pending");
    const vault = readVault(ctx.config.appId, payload.vaultKey);
    (_b = handlers.onSuccess) === null || _b === void 0 ? void 0 : _b.call(handlers, { value: (_c = vault[payload.key]) !== null && _c !== void 0 ? _c : null });
};
